/**
 * One row of recipients: committed addresses as chips, a text input, and the
 * address book behind it.
 *
 * # The input is never the thing that waits
 *
 * Every asynchronous path here is arranged so that it cannot touch what the
 * user is typing. The text is local state updated synchronously on `input`; the
 * completion runs on a timer, writes only to the dropdown, and its result is
 * discarded outright if a newer one has started (`Completions.run` resolves to
 * `null` -- see `lib/contacts.ts` for why that is a return value rather than a
 * convention). A slow, failing or entirely absent address book therefore costs
 * an empty dropdown and nothing else.
 *
 * # Why the text is committed on blur
 *
 * A typed address that was never turned into a chip is an address the send does
 * not have. People type a recipient and click Send, and a field that quietly
 * dropped that last token would send the message to everyone *except* the
 * person just named. So blur commits, and the dropdown suppresses the blur with
 * `mousedown` prevention so that clicking a suggestion is not a race against it.
 */

import { signal } from "@preact/signals";
import { type ComponentChildren } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";

import { addressKey, formatAddress, parseAddress, splitAddressList } from "../lib/compose";
import { Completions, removeContact, type Contact } from "../lib/contacts";
import * as store from "../lib/store";
import type { EmailAddress } from "../lib/types";
import * as Icon from "./Icons";

/**
 * Long enough that typing a name is one request rather than six, short enough
 * that the list is there by the time the eye moves to it.
 */
const DEBOUNCE_MS = 140;

interface Props {
  label: string;
  values: EmailAddress[];
  onChange: (next: EmailAddress[]) => void;
  autofocus?: boolean;
  /** Rendered under the row; used for the Bcc warning. */
  hint?: string;
  /**
   * Rendered at the right end of the row, outside the chip box; used for the
   * Add Cc / Add Bcc toggles on the To line. A slot rather than anything
   * smarter, so this field never knows what the composer put there.
   */
  trailing?: ComponentChildren;
}

/**
 * The chip being dragged, if it came from one of these rows.
 *
 * Module state rather than `dataTransfer`, because the move needs the source
 * row to give the address up, and only the source knows how: the target adds
 * it to its own list and calls `release`. A drag from anywhere else (a file,
 * text from another app) finds this empty and is not a recipient.
 */
export const dragging = signal<{ address: EmailAddress; from: string; release: () => void } | null>(
  null,
);

export function RecipientField({ label, values, onChange, autofocus, hint, trailing }: Props) {
  const id = useId();
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;

  const [text, setText] = useState("");
  const [items, setItems] = useState<Contact[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const input = useRef<HTMLInputElement | null>(null);
  // One channel per field. Sharing one would mean typing in Cc cancels the
  // dropdown standing open over To.
  const completions = useRef<Completions | null>(null);
  completions.current ??= new Completions();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const owned = completions.current;
    return () => {
      clearTimeout(timer.current);
      owned?.cancel();
    };
  }, []);

  function close(): void {
    clearTimeout(timer.current);
    completions.current?.cancel();
    setOpen(false);
    setActive(-1);
    setItems([]);
  }

  function query(prefix: string): void {
    clearTimeout(timer.current);
    if (prefix.trim() === "") {
      close();
      return;
    }
    timer.current = setTimeout(() => {
      void completions.current?.run(prefix).then((found) => {
        // `null` means a later keystroke started its own request. Rendering
        // these rows would be showing results for a prefix already replaced.
        if (found === null) return;
        setItems(found);
        setOpen(found.length > 0);
        setActive(found.length > 0 ? 0 : -1);
      });
    }, DEBOUNCE_MS);
  }

  function add(address: EmailAddress): void {
    const already = values.some((v) => v.email.toLowerCase() === address.email.toLowerCase());
    onChange(already ? values : [...values, address]);
    setText("");
    close();
  }

  /** Turn whatever is typed into a chip. Returns false if it was not an address. */
  function commitText(): boolean {
    const parsed = parseAddress(text);
    if (!parsed) return text.trim() === "";
    add(parsed);
    return true;
  }

  function accept(index: number): void {
    const contact = items[index];
    if (!contact) return;
    // `address` for the header, `displayAddress` for the human, exactly as the
    // daemon documents: they differ for Gmail dot-and-plus spellings, and
    // putting the pretty one in a `To:` header would send to a normalisation
    // the server did not intend.
    add({ name: contact.name, email: contact.address });
  }

  async function stopSuggesting(contact: Contact): Promise<void> {
    setItems((list) => list.filter((c) => c.id !== contact.id));
    try {
      await removeContact(contact.id);
      store.toast(`${contact.displayAddress} will not be suggested again.`);
    } catch {
      store.toast("That suggestion could not be removed.", "error");
    }
  }

  function onKeyDown(e: KeyboardEvent): void {
    switch (e.key) {
      case "ArrowDown":
        if (!open) return;
        e.preventDefault();
        setActive((i) => Math.min(i + 1, items.length - 1));
        return;
      case "ArrowUp":
        if (!open) return;
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        return;
      case "Enter":
        e.preventDefault();
        // Enter must never reach the composer, which would submit a message
        // whose last recipient is still half-typed.
        e.stopPropagation();
        if (open && active >= 0) accept(active);
        else commitText();
        return;
      case "Escape":
        if (!open) return;
        // Only when there is a dropdown to dismiss. Otherwise Escape belongs to
        // the composer, and swallowing it here would trap the user in the form.
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      case "Tab":
        if (open && active >= 0) {
          e.preventDefault();
          accept(active);
          return;
        }
        // Not prevented: the address is taken and focus still moves on.
        commitText();
        return;
      case ",":
      case ";":
        e.preventDefault();
        commitText();
        return;
      case "Delete":
        // Prune the highlighted suggestion. The mouse equivalent is the small
        // cross on the row; this is the keyboard one, and the option's label
        // says so out loud.
        if (open && active >= 0 && items[active]) {
          e.preventDefault();
          void stopSuggesting(items[active]!);
        }
        return;
      case "Backspace":
        if (text === "" && values.length > 0) {
          e.preventDefault();
          onChange(values.slice(0, -1));
        }
        return;
      default:
        return;
    }
  }

  const [dropping, setDropping] = useState(false);
  const accepts = () => dragging.value !== null && dragging.value.from !== label;
  const drop = (e: DragEvent) => {
    setDropping(false);
    const moved = dragging.value;
    if (!moved || moved.from === label) return;
    e.preventDefault();
    dragging.value = null;
    const key = addressKey(moved.address.email);
    if (!values.some((v) => addressKey(v.email) === key)) onChange([...values, moved.address]);
    moved.release();
  };

  return (
    <div class="compose-line rf-line">
      <span class="rf-label" id={`${id}-label`}>
        {label}
      </span>
      <div class="rf">
        {/*
          `data-tauri-drag-region="false"` shields this box from the composer
          fields' `deep` drag region: it is a plain div whose click focuses the
          input, and Tauri's drag script only exempts *interactive* elements --
          without the shield, clicking the empty half of the box would move the
          window instead of placing the caret.
        */}
        <div
          class={dropping ? "rf-box rf-drop" : "rf-box"}
          data-tauri-drag-region="false"
          onClick={() => input.current?.focus()}
          // Drag a chip to another row to move it there: To, Cc and Bcc.
          onDragOver={(e) => {
            if (!accepts()) return;
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
            setDropping(true);
          }}
          onDragLeave={() => setDropping(false)}
          onDrop={drop}
        >
          {values.map((a, i) => (
            <span
              class="chip"
              key={`${a.email}-${i}`}
              draggable
              onDragStart={(e) => {
                dragging.value = {
                  address: a,
                  from: label,
                  release: () => onChange(values.filter((_, j) => j !== i)),
                };
                if (e.dataTransfer) {
                  e.dataTransfer.effectAllowed = "move";
                  // Some engines start no drag without data.
                  e.dataTransfer.setData("text/plain", formatAddress(a));
                }
              }}
              onDragEnd={() => {
                dragging.value = null;
              }}
            >
              <span class="chip-text" title={a.email}>
                {a.name?.trim() || a.email}
              </span>
              <button
                type="button"
                class="chip-x"
                aria-label={`Remove ${formatAddress(a)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(values.filter((_, j) => j !== i));
                }}
              >
                <Icon.Close />
              </button>
            </span>
          ))}
          <input
            ref={input}
            type="text"
            class="rf-input"
            // Now that the desktop webview checks spelling (see
            // src-tauri/spell.rs), an address field would be a row of
            // squiggles: names and domains are not prose.
            spellcheck={false}
            autocorrect="off"
            role="combobox"
            aria-labelledby={`${id}-label`}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            {...(open && active >= 0 ? { "aria-activedescendant": optionId(active) } : {})}
            autocomplete="off"
            autofocus={autofocus}
            value={text}
            placeholder={values.length === 0 ? "name or address" : ""}
            onInput={(e) => {
              const next = (e.target as HTMLInputElement).value;
              setText(next);
              query(next);
            }}
            onKeyDown={onKeyDown}
            onPaste={(e) => {
              const pasted = e.clipboardData?.getData("text") ?? "";
              // A single address takes the ordinary input path. Only a
              // *list* is intercepted: `parseAddress` refuses anything with
              // a separator in it, so a pasted "a@x.org, b@y.org" used to
              // sit in the input as unparseable text and quietly miss the
              // send -- the exact loss this field exists to prevent.
              if (!/[,;\n\r]/u.test(pasted)) return;
              e.preventDefault();
              const next = [...values];
              const leftovers: string[] = [];
              for (const part of splitAddressList(`${text} ${pasted}`)) {
                const parsed = parseAddress(part);
                if (!parsed) {
                  leftovers.push(part);
                } else if (
                  !next.some((v) => v.email.toLowerCase() === parsed.email.toLowerCase())
                ) {
                  next.push(parsed);
                }
              }
              onChange(next);
              // Anything unparseable stays in the input where it can be seen
              // and fixed, rather than vanishing.
              setText(leftovers.join(", "));
              close();
            }}
            onBlur={() => {
              // Whatever is typed becomes a chip, so it cannot be lost by
              // clicking straight to Send.
              commitText();
              close();
            }}
          />
        </div>

        <ul
          class="rf-list"
          id={listId}
          role="listbox"
          aria-label={`${label} suggestions`}
          hidden={!open}
          // Keeps the input focused, so clicking a row is a choice rather than
          // a blur that commits half-typed text and closes the list underneath
          // the pointer.
          onMouseDown={(e) => e.preventDefault()}
        >
          {items.map((c, i) => (
            <li
              key={c.id}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              class={`rf-opt${i === active ? " active" : ""}`}
              aria-label={`${c.name ? `${c.name}, ` : ""}${c.displayAddress}. Press Delete to stop suggesting this address.`}
              onMouseEnter={() => setActive(i)}
              onClick={() => accept(i)}
            >
              <span class="rf-opt-main">
                {c.name && <strong>{c.name}</strong>}
                <span class="rf-opt-addr">{c.displayAddress}</span>
              </span>
              {c.tier === "explicit" && (
                <span class="rf-opt-tier" title="You added this one yourself">
                  saved
                </span>
              )}
              <span
                class="rf-opt-x"
                // The keyboard route to the same action is the Delete key, and
                // the option's own label announces it. A real button nested in
                // a `role="option"` would be an interactive element inside a
                // listbox item, which no screen reader is obliged to reach.
                aria-hidden="true"
                title="Stop suggesting this address"
                onClick={(e) => {
                  e.stopPropagation();
                  void stopSuggesting(c);
                }}
              >
                <Icon.Close />
              </span>
            </li>
          ))}
        </ul>
      </div>
      {trailing && <span class="rf-trailing">{trailing}</span>}
      {hint && <span class="rf-hint">{hint}</span>}
    </div>
  );
}
